import { Link, useNavigate } from 'react-router-dom';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import {
    ChangeEvent, ReactElement, useEffect, useState,
} from 'react';
import {
    Alert,
    Button,
    Modal,
    Row,
    Col,
    Table,
} from 'react-bootstrap';
import useAccountContext from '../../../authentication/hooks';
import BlockUISpinner from '../../BlockUISpinner';
import PrimaryButton from '../../Buttons/PrimaryButton';
import ButtonGroup from '../../Buttons/ButtonGroup';
import { OrganisationDto, OrganisationsClient, UsersClient } from '../../../api/web-api-client';
import { tokenRequest } from '../../../authentication/authConfig';
import NotificationMessage from '../../Alert/NotificationMessage';
import { clearBranchModalNotification, getBranchModalNotification } from '../../../storage/notification';
import AppLogger from '../../../instrumentation/AppLogger';
import { BranchSelectionModalMode } from './enums';

interface SavingBranchSelectorErrorProps {
    showError: boolean,
}

const showBranchModalMessage = (message: JSX.Element | null) => (
    <>
        {message && (
            <Row className='mb-4'>
                <Col>
                    {message}
                </Col>
            </Row>
        )}
    </>
);

const setNotification = () => {
    const branchModalNotification = getBranchModalNotification();
    return (
        branchModalNotification
            ? (
                <NotificationMessage
                    id='notif-message-1'
                    canClose
                    onClose={clearBranchModalNotification}
                    {...branchModalNotification}
                />
            )
            : null);
};

const SavingBranchSelectorError = (props: SavingBranchSelectorErrorProps) : ReactElement | null => {
    const { showError } = props;
    if (showError) {
        return (
            <Row className='mb-4'>
                <Col>
                    <Alert variant='danger' className='d-flex' role='alert' aria-live='assertive'>
                        <div className='d-flex justify-content-center justify-content-md-start mb-3 mb-md-0'>
                            <div className='bgCircle me-3'>
                                <i className='icon-warning' aria-hidden='true' />
                            </div>
                        </div>
                        <div>
                            <p className='mb-3'>Error trying to save default branch/location. Try again later!</p>
                        </div>
                    </Alert>
                </Col>
            </Row>
        );
    }
    return null;
};

interface SaveButtonProps {
    onClick: () => void,
}

const SaveButton = (props: SaveButtonProps) : JSX.Element => {
    const { onClick } = props;
    return (
        <PrimaryButton
            data-testid='save-button'
            onClick={onClick}
            className='ms-md-auto'
        >
            Save and continue
        </PrimaryButton>
    );
};

interface BranchSelectorButtonsProps {
    left: any,
    right: any
}

const BranchSelectorButtons = (props: BranchSelectorButtonsProps) => {
    const { left, right } = props;
    return (
        <ButtonGroup
            left={left}
            right={right}
        />
    );
};

const BranchSelectorModal = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isModalDataLoading, setIsModalDataLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [savingBranchSelectorError, setSavingBranchSelectorError] = useState(false);
    const { inProgress, accounts, instance } = useMsal();
    const [selectedBranch, setSelectedBranch] = useState<number | undefined>();
    const [selectedOrganisation, setSelectedOrganisation] = useState<string | undefined>();
    const [selectedTradingName, setSelectedTradingName] = useState<string | undefined>();
    const [selectedBranchName, setSelectedBranchName] = useState<string | undefined>();
    const [selectedABN, setSelectedABN] = useState<string | undefined>();
    const [selectedCRMGuid, setSelectedCRMGuid] = useState<string | undefined>();
    const [branches, setBranches] = useState<OrganisationDto[] | undefined>();
    const branchModalMessage = setNotification();
    const accountContext = useAccountContext();
    const [sortConfig, setSortConfig] = useState<{ key: keyof OrganisationDto | null; direction: 'asc' | 'desc' }>({ key: 'branchOrLocationName', direction: 'asc' });

    const defaultOrganisationIdSet = accountContext?.details?.defaultOrganisationId !== null;
    const branchSelectionModalMode = accountContext?.details?.branchSelectionModalMode;

    const navigate = useNavigate();

    const locationOnModalSave = (() => {
        switch (branchSelectionModalMode) {
            case BranchSelectionModalMode.RFQSelectOrg:
                if (accountContext?.details?.callingPath !== undefined) {
                    return accountContext?.details?.callingPath;
                }
                return '/';
            default:
                return '/';
        }
    })();

    const onContinueBranchSelectorModal = async () => {
        if (inProgress === InteractionStatus.None && accounts.length > 0) {
            setIsSaving(true);
            const client = new UsersClient();
            const tokenResult = await instance.acquireTokenSilent({
                ...tokenRequest,
                account: accounts[0],
            });
            client.setAuthToken(tokenResult.accessToken);
            try {
                if (branchSelectionModalMode === BranchSelectionModalMode.RFQSelectOrg) {
                    await client.setDefaultOrganisation({ defaultOrganisationId: selectedBranch, rfqId: accountContext?.details?.rfqId });
                    // force page reload
                    window.location.reload();
                } else if (branchSelectionModalMode === BranchSelectionModalMode.SelectAndEditOrg) {
                    await client.setDefaultOrganisation({ defaultOrganisationId: selectedBranch });
                }
                if (accountContext) {
                    await accountContext.setDefaultOrganisationId(selectedBranch, selectedCRMGuid);
                    if (selectedOrganisation !== undefined) {
                        await accountContext.setOrganisationAndBranch(selectedOrganisation, selectedTradingName !== undefined ? selectedTradingName : '', selectedBranchName !== undefined ? selectedBranchName : '');
                    }
                }
                if (!accountContext?.details?.accountCreationCompleted) {
                    accountContext?.setCompleted();
                    accountContext?.setDefaultOrganisationId(selectedBranch, selectedCRMGuid);
                    accountContext?.setTargetOrganisation(selectedOrganisation!, selectedABN!);
                }
                accountContext?.setShowBranchSelector(false);
            } catch (error) {
                AppLogger.error('Failed to select organisation', error as Error);
                setSavingBranchSelectorError(true);
            } finally {
                setIsSaving(false);
                accountContext?.setShowBranchSelector(false);
                // Reload dashboard page to grab new accountContext etc details
                navigate(locationOnModalSave);
            }
            setIsSaving(false);
            accountContext?.setShowBranchSelector(false);
            clearBranchModalNotification();
        }
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>, branchId: number | undefined, organisationName: string | undefined, tradingName: string | undefined, branchName: string | undefined, abn: string | undefined, crmGuid: string | undefined) => {
        setSelectedBranch(branchId);
        setSelectedOrganisation(organisationName);
        setSelectedTradingName(tradingName);
        setSelectedBranchName(branchName);
        setSelectedABN(abn);
        setSelectedCRMGuid(crmGuid);
    };

    useEffect(() => {
        const loadBranches = async () => {
            if (accountContext
                && accountContext.details
                && accountContext.details.abn) {
                const client = new OrganisationsClient();
                const tokenResult = await instance.acquireTokenSilent({
                    ...tokenRequest,
                    account: accounts[0],
                });
                client.setAuthToken(tokenResult.accessToken);

                try {
                    setIsModalDataLoading(true); // Modal data
                    const result = await client.getOrganisationsByABN(accountContext.details.abn, true);
                    setBranches(result);
                    if (accountContext.details.defaultOrganisationId !== null) {
                        setSelectedBranch(accountContext.details.defaultOrganisationId);
                        setSelectedCRMGuid(accountContext.details.organisationCRMGuid);
                        setSelectedTradingName(accountContext.details.trading);
                        setSelectedBranchName(accountContext.details.branch);
                    } else {
                        setSelectedBranch(result[0].organisationId);
                        setSelectedTradingName(result[0].businessOrTradingName);
                        setSelectedBranchName(result[0].branchOrLocationName);
                        setSelectedABN(result[0].abn);
                        setSelectedCRMGuid(result[0].crmGuid);
                    }
                    setSelectedOrganisation(result[0].name);
                } catch (error) {
                    AppLogger.error('Failed to load branches', error as Error);
                    setSavingBranchSelectorError(true);
                } finally {
                    setIsModalDataLoading(false);
                }
                setIsModalDataLoading(false);
            }
        };
        loadBranches();
    }, [accountContext, accounts, instance, accountContext, accountContext?.details?.defaultOrganisationId]);

    const handleClose = () => {
        accountContext?.setShowBranchSelector(false);
        clearBranchModalNotification();
    };

    const saveButton = () => (
        <SaveButton onClick={onContinueBranchSelectorModal} />
    );

    const closeButton = () => (
        <Button
            data-testid='exit-portal-button'
            onClick={handleClose}
            variant='tertiary'
            className='me-md-auto order-2 order-md-0'
        >
            <i className='icon-close me-1' aria-hidden='true' />
            Cancel
        </Button>
    );

    const sortData = (key: keyof OrganisationDto) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }

        const sorted = branches?.sort((a, b) => {
            const aValue = a[key]?.toString().toLowerCase();
            const bValue = b[key]?.toString().toLowerCase();

            if (aValue! < bValue!) return direction === 'asc' ? -1 : 1;
            if (aValue! > bValue!) return direction === 'asc' ? 1 : -1;

            return 0;
        });

        setBranches(sorted);
        setSortConfig({ key, direction });
    };

    const getArrow = (key: keyof OrganisationDto) => {
        if (sortConfig.key !== key) {
            return (
                <i className='icon-chevron-up text-muted' aria-hidden='true' role='presentation' />
            );
        }
        return sortConfig.direction === 'asc'
            ? <i className='icon-chevron-down text-primary' aria-hidden='true' role='presentation' />
            : <i className='icon-chevron-up text-primary' aria-hidden='true' role='presentation' />;
    };

    return (
        <Modal
            size='lg'
            show={accountContext?.details?.showBranchSelector}
            aria-modal={accountContext?.details?.showBranchSelector}
            enforceFocus={accountContext?.details?.showBranchSelector}
            aria-live='assertive'
            // aria-atomic='true'
            tabIndex={-1}
            backdrop='static'
            keyboard={false}
            data-testid='prompt-branchselector-modal'
            // aria-labelledby='modal-select-branch'
            onHide={handleClose}
        >
            <Modal.Header closeButton={defaultOrganisationIdSet}>
                {branchSelectionModalMode === BranchSelectionModalMode.SelectAndEditOrg && (
                    <Modal.Title
                        id='modal-select-branch'
                        as='h3'
                    >
                        {'Manage your branch or location '}
                        <span className='visually-hidden'> (modal dialog)</span>
                    </Modal.Title>
                )}
                {branchSelectionModalMode === BranchSelectionModalMode.RFQSelectOrg && (
                    <Modal.Title
                        id='modal-select-branch'
                        as='h3'
                    >
                        {'Change branch/location '}
                        <span className='visually-hidden'> (modal dialog)</span>
                    </Modal.Title>
                )}
            </Modal.Header>
            <Modal.Body>
                {branchSelectionModalMode === BranchSelectionModalMode.SelectAndEditOrg && (
                    <Row>
                        <Col>
                            <p>
                                Branches or locations are available for your organisation and can be used to help manage NMI Services portal requests.
                            </p>
                            <p className='mb-0'>With this interface, you can:</p>
                            <ul className='mb-4'>
                                <li>
                                    <strong>Change</strong>
                                    {' the currently managed branch or location'}
                                </li>
                                <li>
                                    <strong>Edit</strong>
                                    {' details for any branch or location'}
                                </li>
                                <li>
                                    <strong>Add</strong>
                                    {' a new branch or location'}
                                </li>
                                <li>
                                    <strong>Cancel</strong>
                                    {' anytime without saving changes'}
                                </li>
                            </ul>
                        </Col>
                    </Row>
                )}
                {branchSelectionModalMode === BranchSelectionModalMode.RFQSelectOrg && (
                    <Row>
                        <Col>
                            <p>
                                <strong>Select default branch or location name to manage</strong>
                            </p>
                        </Col>
                    </Row>
                )}
                <SavingBranchSelectorError showError={savingBranchSelectorError} />
                {showBranchModalMessage(branchModalMessage)}
                <Row className='mb-4'>
                    <Col>
                        <div
                            aria-labelledby='caption-org-select-list'
                        >
                            <fieldset
                                className='w-100'
                                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                                role='radiogroup'
                            >
                                <legend className='h4 visually-hidden'>
                                    {'Select a branch or location to view currently managing, edit or add a new one for '}
                                </legend>
                                <p>
                                    {'Entity name: '}
                                    <strong>
                                        {selectedOrganisation}
                                    </strong>
                                </p>
                                <Table
                                    id='select-org-table'
                                    data-testid='select-org-table'
                                    className='table-sm table-responsive-stack small mb-0'
                                    aria-live='off'
                                >
                                    <caption id='caption-org-select-list' className='visually-hidden'>
                                        Add, edit or choose your default branch/location from the table list for this organisation
                                        (column headers with buttons are sortable).
                                    </caption>
                                    <thead key='headKeyForThead'>
                                        <tr className='table-active'>
                                            <th
                                                scope='col'
                                                aria-sort={sortConfig.direction === 'asc' ? 'ascending' : 'descending'}
                                                className='w-50 align-middle sortable-column'
                                            >
                                                <span className='visually-hidden text-nowrap'>Set as default</span>
                                                <Button
                                                    variant='flat'
                                                    data-column-index='0'
                                                    onClick={() => sortData('businessListName')}
                                                    className='ms-2 p-0 px-2 body-text fs-6 fw-bold'
                                                    title='Change sort order'
                                                >
                                                    <span className='me-2'>
                                                        Branch/location name
                                                    </span>
                                                    <span className='sort-arrow' aria-hidden='true' role='presentation'>{getArrow('businessListName')}</span>
                                                </Button>
                                            </th>
                                            <th scope='col' className='w-50 align-middle text-nowrap'>Location</th>
                                            {branchSelectionModalMode === BranchSelectionModalMode.SelectAndEditOrg && (
                                                <th scope='col' className='align-middle text-nowrap'><span className='visually-hidden'>Action</span></th>
                                            )}
                                        </tr>
                                    </thead>
                                    {isModalDataLoading
                                        ? (
                                            <tbody key='tBodyKey'>
                                                <tr>
                                                    <td colSpan={3}>
                                                        <BlockUISpinner partial>
                                                            <p>Loading data...</p>
                                                        </BlockUISpinner>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        )
                                        : (
                                            <>
                                                {branches !== undefined && branches?.length > 0 ? (
                                                    <tbody key='tBodyKey'>
                                                        {branches.map((branch, index) => (
                                                            <tr key={branch.organisationId}>
                                                                <td className='pe-0' data-header='Set as default'>
                                                                    {/* To do - replace this with RadioButton component */}
                                                                    <div className='form-field-container mb-0' tabIndex={-1}>
                                                                        <div className='radio-button'>
                                                                            <input
                                                                                type='radio'
                                                                                id={`branch${index}`}
                                                                                name='branchSelector'
                                                                                value={branch.organisationId}
                                                                                checked={branch.organisationId === selectedBranch}
                                                                                onChange={
                                                                                    (e) => handleChange(e, branch.organisationId, branch.name, branch.businessOrTradingName, branch.branchOrLocationName, branch.abn, branch.crmGuid)
                                                                                }
                                                                            />
                                                                            <label htmlFor={`branch${index}`} className='form-field flex-column' title='Set as default'>
                                                                                {/* <span className='visually-hidden'>
                                                                                    {'Set as default '}
                                                                                </span> */}
                                                                                <span className='text-break'>{branch.businessListName}</span>
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td data-header='Location' className='text-break'>
                                                                    <span>
                                                                        {`${branch.streetAddress?.suburb}, ${branch.streetAddress?.state}`}
                                                                    </span>
                                                                </td>
                                                                {branchSelectionModalMode === BranchSelectionModalMode.SelectAndEditOrg && (
                                                                    <td>
                                                                        <span>
                                                                            <Link
                                                                                to={`/update-organisation/${branch.organisationId}`}
                                                                                onClick={handleClose}
                                                                                aria-label='Edit details for this branch/location'
                                                                            >
                                                                                Edit
                                                                            </Link>
                                                                        </span>
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                ) : (
                                                    <tbody key='tBodyKey'>
                                                        <tr>
                                                            <td colSpan={3}>No branch/locations available. Please create a new one.</td>
                                                        </tr>
                                                    </tbody>
                                                )}
                                            </>
                                        )}

                                </Table>
                            </fieldset>
                        </div>
                    </Col>
                </Row>
                {branchSelectionModalMode === BranchSelectionModalMode.SelectAndEditOrg && (
                    <Row>
                        <Col>
                            <Link
                                data-testid='open-branch-add-button'
                                to='/add-branch'
                                className='btn btn-flat btn-tertiary'
                                onClick={handleClose}
                            >
                                <i className='icon-add fs-2 me-2' aria-hidden='true' />
                                Add branch or location
                            </Link>
                        </Col>
                    </Row>
                )}
                {isLoading && (
                    <BlockUISpinner>
                        <p>Loading...</p>
                    </BlockUISpinner>
                )}
                {isSaving && (
                    <BlockUISpinner>
                        <p>Saving...</p>
                    </BlockUISpinner>
                )}
            </Modal.Body>
            <Modal.Footer className='d-inline'>
                <BranchSelectorButtons
                    left={() => defaultOrganisationIdSet && closeButton()}
                    right={() => saveButton()}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default BranchSelectorModal;
